import { useMemo, useState } from "react";
import { mirrors } from "@/mocks/home";
import { statusConfig } from "./status";

const filters = ["全部", "官方", "加速", "经典", "海外", "备份", "维护中"];

export default function MirrorIndex() {
  const [keyword, setKeyword] = useState("");
  const [active, setActive] = useState("全部");
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const list = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return mirrors.filter((m) => {
      const tagHit =
        active === "全部" ||
        m.tags.includes(active) ||
        (active === "维护中" && m.status === "maintenance");
      const kwHit =
        !kw ||
        m.title.toLowerCase().includes(kw) ||
        m.host.toLowerCase().includes(kw) ||
        m.summary.toLowerCase().includes(kw);
      return tagHit && kwHit;
    });
  }, [keyword, active]);

  const handleCopy = (id: string, host: string) => {
    setCopiedId(id);
    window.setTimeout(() => setCopiedId(null), 1600);
    if (navigator.clipboard) {
      navigator.clipboard.writeText(host).catch(() => undefined);
    }
  };

  return (
    <section id="mirrors" className="w-full bg-background-100 py-14 md:py-20">
      <div className="w-full max-w-[1180px] mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="text-xs tracking-[0.2em] text-primary-700 uppercase">Mirror Index</p>
            <h4 className="mt-2 font-heading text-2xl md:text-3xl text-foreground-950">
              <a href="#mirrors" className="cursor-pointer hover:text-primary-700 transition-colors">
                镜像站点索引
              </a>
            </h4>
            <p className="mt-3 text-sm text-foreground-600 max-w-2xl leading-relaxed">
              主站之外的所有入口都收在这里。支持搜索域名、按线路筛选，挑一条最顺畅的打开即可。
            </p>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-background-50 border border-background-200 w-full md:w-72">
            <i className="ri-search-line text-foreground-400 text-base"></i>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索域名或关键词"
              className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
            />
            {keyword && (
              <button
                type="button"
                aria-label="清空搜索"
                onClick={() => setKeyword("")}
                className="w-5 h-5 flex items-center justify-center text-foreground-400 cursor-pointer"
              >
                <i className="ri-close-circle-fill"></i>
              </button>
            )}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {filters.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setActive(f)}
              className={`px-3.5 py-1.5 rounded-full text-xs whitespace-nowrap cursor-pointer border transition-colors ${
                active === f
                  ? "bg-primary-500 text-background-50 border-primary-500"
                  : "bg-background-50 text-foreground-700 border-background-300 hover:border-primary-300 hover:text-primary-700"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((m) => {
            const cfg = statusConfig[m.status];
            return (
              <article
                key={m.id}
                className="flex flex-col rounded-lg bg-background-50 border border-background-200 p-5 hover:border-primary-300 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <h5 className="font-heading text-base text-foreground-950 leading-snug">{m.title}</h5>
                  <span className={`shrink-0 px-2 py-0.5 rounded-full text-[11px] border ${cfg.badge}`}>
                    {cfg.label}
                  </span>
                </div>

                <div className="mt-3 flex items-center gap-2 text-sm text-foreground-700 font-mono">
                  <i className="ri-link text-primary-600"></i>
                  <span className="truncate">{m.host}</span>
                </div>

                <p className="mt-3 text-[13px] text-foreground-600 leading-relaxed flex-1">{m.summary}</p>

                <div className="mt-4 flex flex-wrap gap-1.5">
                  {m.tags.map((t) => (
                    <span
                      key={t}
                      className="px-2 py-0.5 rounded-md text-[11px] bg-secondary-100 text-secondary-900 border border-secondary-200"
                    >
                      {t}
                    </span>
                  ))}
                </div>

                <div className="mt-4 pt-4 border-t border-background-200 flex items-center justify-between gap-2">
                  <div className="text-[11px] text-foreground-500 flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-map-pin-line"></i>
                      {m.region}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-speed-line"></i>
                      {m.latency}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleCopy(m.id, m.host)}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] whitespace-nowrap cursor-pointer bg-secondary-500 text-background-50 hover:bg-secondary-600 transition-colors"
                  >
                    <i className={copiedId === m.id ? "ri-check-line" : "ri-file-copy-line"}></i>
                    {copiedId === m.id ? "已复制" : "复制"}
                  </button>
                </div>

                <p className="mt-2 text-[11px] text-foreground-400">更新于 {m.updated}</p>
              </article>
            );
          })}
        </div>

        {list.length === 0 && (
          <div className="mt-8 rounded-lg border border-dashed border-background-300 py-14 text-center">
            <i className="ri-search-eye-line text-3xl text-foreground-300"></i>
            <p className="mt-3 text-sm text-foreground-500">没有匹配的入口，换个关键词或点「全部」看看。</p>
          </div>
        )}
      </div>
    </section>
  );
}